// hooks/useSessionManager.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabaseService } from '../SupabaseService';

interface SessionManagerConfig {
  inactivityTimeout?: number;
  warningTime?: number;
  editingTimeout?: number;
  checkInterval?: number;
}

interface SessionState {
  showInactivityWarning: boolean;
  warningCountdown: number;
  editingTimeouts: Map<number, NodeJS.Timeout>;
}

interface SessionManagerReturn {
  showInactivityWarning: boolean;
  warningCountdown: number;
  extendSession: () => void;
  handleInactivityLogout: () => void;
  startEditingTimeout: (caseId: number, onTimeout: (caseId: number) => void) => void;
  clearEditingTimeout: (caseId: number) => void;
  clearAllEditingTimeouts: () => void;
}

const defaultConfig: Required<SessionManagerConfig> = {
  inactivityTimeout: 60 * 60 * 1000, // 1 time
  warningTime: 5 * 60 * 1000, // 5 minutter varsel
  editingTimeout: 30 * 60 * 1000, // 30 minutter for redigering
  checkInterval: 60 * 1000, // Sjekk hvert minutt
};

export const useSessionManager = (
  isAuthenticated: boolean,
  onLogout: () => void,
  showInfo: (message: string, title?: string) => void,
  showWarning: (message: string, title?: string) => void,
  config: SessionManagerConfig = {}
): SessionManagerReturn => {
  const cfg = { ...defaultConfig, ...config };
  
  const [sessionState, setSessionState] = useState<SessionState>({
    showInactivityWarning: false,
    warningCountdown: 0,
    editingTimeouts: new Map(),
  });

  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Oppdater siste aktivitet
  const updateLastActivity = useCallback(() => {
    const now = new Date().toISOString();
    localStorage.setItem('hagia-last-activity', now);
  }, []);

  // Sjekk inaktivitet
  const checkInactivity = useCallback(() => {
    const lastActivity = localStorage.getItem('hagia-last-activity');
    if (!lastActivity) {
      updateLastActivity();
      return;
    }

    const now = new Date().getTime();
    const lastActivityTime = new Date(lastActivity).getTime();
    const timeSinceLastActivity = now - lastActivityTime;

    // Hvis mer enn 55 minutter inaktiv, vis varsel
    if (timeSinceLastActivity >= (cfg.inactivityTimeout - cfg.warningTime) && !sessionState.showInactivityWarning) {
      handleInactivityWarning();
    }
    
    // Hvis mer enn 1 time inaktiv, logg ut
    if (timeSinceLastActivity >= cfg.inactivityTimeout) {
      handleInactivityLogout();
    }
  }, [cfg.inactivityTimeout, cfg.warningTime, sessionState.showInactivityWarning]);

  // Håndter inaktivitetsvarsel
  const handleInactivityWarning = useCallback(() => {
    setSessionState(prev => ({
      ...prev,
      showInactivityWarning: true,
      warningCountdown: Math.floor(cfg.warningTime / 1000),
    }));
    
    // Start nedtelling
    const countdownInterval = setInterval(() => {
      setSessionState(prev => ({
        ...prev,
        warningCountdown: Math.max(0, prev.warningCountdown - 1),
      }));
    }, 1000);
    countdownIntervalRef.current = countdownInterval;

    // Automatisk logout etter warning time
    const autoLogoutTimer = setTimeout(() => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      handleInactivityLogout();
    }, cfg.warningTime);

    warningTimerRef.current = autoLogoutTimer;
  }, [cfg.warningTime]);

  // Håndter inaktivitets-logout
  const handleInactivityLogout = useCallback(async () => {
    try {
      await supabaseService.logSystemEvent('AUTO_LOGOUT_INACTIVITY', {
        user: localStorage.getItem('hagia-user-name'),
        login_time: localStorage.getItem('hagia-login-time'),
        logout_time: new Date().toISOString(),
        reason: 'Automatisk utlogging etter 1 time inaktivitet'
      });
    } catch (error) {
      console.error('Feil ved automatisk utlogging:', error);
    }
    
    // Rens opp og logg ut
    clearAllTimers();
    localStorage.removeItem("hagia-last-activity");
    onLogout();
  }, [onLogout, showInfo]);

  // Forleng session
  const extendSession = useCallback(() => {
    updateLastActivity();
    setSessionState(prev => ({
      ...prev,
      showInactivityWarning: false,
      warningCountdown: 0,
    }));
    
    // Rens opp warning timere
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, [updateLastActivity]);

  // Rens alle timere
  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearInterval(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    // Rens alle redigeringstimere
    sessionState.editingTimeouts.forEach(timeout => clearTimeout(timeout));
    setSessionState(prev => ({
      ...prev,
      editingTimeouts: new Map(),
    }));
  }, [sessionState.editingTimeouts]);

  // Start redigeringstimeout
  const startEditingTimeout = useCallback((caseId: number, onTimeout: (caseId: number) => void) => {
    // Rens eksisterende timeout for denne casen
    const existingTimeout = sessionState.editingTimeouts.get(caseId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Start ny timeout
    const timeoutId = setTimeout(async () => {
      try {
        await supabaseService.logSystemEvent('EDITING_TIMEOUT', {
          user: localStorage.getItem('hagia-user-name'),
          case_id: caseId,
          timeout_duration: '30 minutes',
          timestamp: new Date().toISOString()
        });
        showWarning("Redigeringsøkten er tidsavbrutt etter 30 minutter", "Timeout");
        onTimeout(caseId);
      } catch (error) {
        console.error('Feil ved timeout logging:', error);
        onTimeout(caseId);
      }
      
      // Fjern timeout fra map
      setSessionState(prev => {
        const newTimeouts = new Map(prev.editingTimeouts);
        newTimeouts.delete(caseId);
        return { ...prev, editingTimeouts: newTimeouts };
      });
    }, cfg.editingTimeout);

    // Lagre timeout i map
    setSessionState(prev => {
      const newTimeouts = new Map(prev.editingTimeouts);
      newTimeouts.set(caseId, timeoutId);
      return { ...prev, editingTimeouts: newTimeouts };
    });

    // Lagre også i localStorage for persistering
    localStorage.setItem(`hagia-editing-timeout-${caseId}`, timeoutId.toString());
    localStorage.setItem(`hagia-editing-start-${caseId}`, new Date().toISOString());
  }, [cfg.editingTimeout, sessionState.editingTimeouts, showWarning]);

  // Rens redigeringstimeout
  const clearEditingTimeout = useCallback((caseId: number) => {
    const timeout = sessionState.editingTimeouts.get(caseId);
    if (timeout) {
      clearTimeout(timeout);
      setSessionState(prev => {
        const newTimeouts = new Map(prev.editingTimeouts);
        newTimeouts.delete(caseId);
        return { ...prev, editingTimeouts: newTimeouts };
      });
    }
    
    // Rens også localStorage
    localStorage.removeItem(`hagia-editing-timeout-${caseId}`);
    localStorage.removeItem(`hagia-editing-start-${caseId}`);
  }, [sessionState.editingTimeouts]);

  // Rens alle redigeringstimere
  const clearAllEditingTimeouts = useCallback(() => {
    sessionState.editingTimeouts.forEach((timeout, caseId) => {
      clearTimeout(timeout);
      localStorage.removeItem(`hagia-editing-timeout-${caseId}`);
      localStorage.removeItem(`hagia-editing-start-${caseId}`);
    });
    
    setSessionState(prev => ({
      ...prev,
      editingTimeouts: new Map(),
    }));
  }, [sessionState.editingTimeouts]);

  const resetInactivityWarning = useCallback(() => {
  // Rens alle timere
  if (warningTimerRef.current) {
    clearTimeout(warningTimerRef.current);
    warningTimerRef.current = null;
  }
  if (countdownIntervalRef.current) {
    clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = null;
  }
  
  // Nullstill state HELT
  setSessionState({
    showInactivityWarning: false,
    warningCountdown: 0,
    editingTimeouts: new Map(),
  });
  
  // Oppdater siste aktivitet
  updateLastActivity();
  
  console.log('✅ Inaktivitetsvarsel nullstilt');
}, [updateLastActivity]);

  // Hovedeffekt for inaktivitetstimer
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      return;
    }

    // Sett initial aktivitet
    updateLastActivity();
    
    // Start inaktivitetstimer
    const timer = setInterval(checkInactivity, cfg.checkInterval);
    inactivityTimerRef.current = timer;

    return () => {
      clearInterval(timer);
    };
  }, [isAuthenticated, checkInactivity, cfg.checkInterval, updateLastActivity]);

  // Aktivitets-listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      if (isAuthenticated && !sessionState.showInactivityWarning) {
        updateLastActivity();
      }
    };

    activities.forEach(activity => {
      document.addEventListener(activity, handleActivity, true);
    });

    return () => {
      activities.forEach(activity => {
        document.removeEventListener(activity, handleActivity, true);
      });
    };
  }, [isAuthenticated, updateLastActivity, sessionState.showInactivityWarning]);

  // Cleanup ved unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  return {
    showInactivityWarning: sessionState.showInactivityWarning,
    warningCountdown: sessionState.warningCountdown,
    extendSession,
    handleInactivityLogout,
    startEditingTimeout,
    clearEditingTimeout,
    clearAllEditingTimeouts,
    resetInactivityWarning,
  };
};